import { Button } from "components/DesignSystem/Button/Button";
import { useAppSelector } from "hooks/useAppSelector";
import { useToast } from "hooks/useToast";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import BooksPreOnboarding from "static/images/BooksPreOnboarding.svg";
import {
  useCreateProductOnboardingMutation,
  useGetOnboardingProductsQuery,
} from "store/apis/productOnboarding";
import { openOnboardingWidget } from "store/slices/onboardingWidget";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { BackendError } from "types/utils/error";
import { OnboardingHomeCard } from "components/OnboardingHomeCard/OnboardingHomeCard";
import * as PRODUCT_ONBOARDING from "constants/productOnboardings";
import { LargeCard } from "components/home/Home";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { ReferralBanner } from "components/home/TaxComponents/ReferralBanner";
import { GetStartedCard, OnboardingStep } from "./GetStartedCard";
import { WhatYouGetCarousel } from "./WhatYouGetCarousel";

export const BooksOnboarding = () => {
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast } = useToast();
  const currentGroup = useCurrentGroupContext();
  const query = useQuery();
  const isBooksOnboarding = query.get("books_onboarding");
  const { update } = useUpdateQuery();
  const entityIdOnSelect = useAppSelector(
    (state) => state.onboardingWidget.entityId
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;

  const [createBooksOnboarding, { isLoading }] =
    useCreateProductOnboardingMutation();

  const { data: products } = useGetOnboardingProductsQuery(
    { groupId: groupId! },
    { skip: !groupId }
  );

  const platformOnboarding = products?.find(
    (p) => p.name === PRODUCT_ONBOARDING.BOOKS
  );

  const pushOnboardingWidgetParam = () => {
    update({ query: "onboarding_widget", value: "open" });
  };

  const openBooksOnboarding = async () => {
    try {
      await createBooksOnboarding({
        groupId: currentGroup.uuid,
        productId: process.env.PUBLIC_BOOKS_ONBOARDING_PRODUCT_ID!,
      }).unwrap();
      dispatch(
        openOnboardingWidget({ product: PRODUCT_ONBOARDING.BOOKS, entityId })
      );
      pushOnboardingWidgetParam();
    } catch (error: any) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  useEffect(() => {
    if (isBooksOnboarding) {
      dispatch(
        openOnboardingWidget({ product: PRODUCT_ONBOARDING.BOOKS, entityId })
      );
      update({ query: "onboarding_widget", value: "open" });
      update({ query: "books_onboarding", value: null });
    }
  }, [isBooksOnboarding]);

  const booksProduct = products?.find(
    (p) => p.name === PRODUCT_ONBOARDING.BOOKS
  );

  const getStepDescription = (stepKey: string) => {
    switch (stepKey) {
      case "bookkeeping_preferences":
        return "Configure Inkle Books with your accounting details";

      case "choose_bookkeeping_plan":
        return "Pick the plan that fits your business";

      case "connect_bank":
        return "Link your banks, revenue & payroll apps to start syncing";

      case "share_bank_account_preferences":
        return "Give Inkle secure access to your bank data.";

      default:
        return "";
    }
  };

  const visibleSteps =
    booksProduct?.steps
      ?.filter((step) => !step.is_hidden)
      ?.sort((a, b) => a.order - b.order) || [];

  const onboardingSteps: OnboardingStep[] = visibleSteps.map((step) => ({
    title: step.name,
    description: getStepDescription(step.step_key),
    onClick: openBooksOnboarding,
    isLoading,
    status: step.status as OnboardingStep["status"],
  }));

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <GetStartedCard
        steps={onboardingSteps}
        isLoading={platformOnboarding?.status === "COMPLETE"}
      />
      {platformOnboarding?.status !== "COMPLETE" && <WhatYouGetCarousel />}
      <ReferralBanner />
    </div>
  );
};
